import React from 'react';
import styles from './SilentPicker.module.scss';
import { PickerItem as PickerItemType } from './types';
import { PickerItem } from './PickerItem';
import { PickerSectionHeader } from './PickerSectionHeader';
import { PickerDivider } from './PickerDivider';

interface PickerPopoverProps {
  items: PickerItemType[];
  selectedKey?: string;
  focusedIndex: number;
  pickerId: string;
  label?: string;
  popoverStyle: React.CSSProperties;
  listboxRef: React.RefObject<HTMLUListElement>;
  onSelect: (item: PickerItemType, sectionLabel?: string) => void;
  onItemKeyDown: (
    event: React.KeyboardEvent,
    item: PickerItemType,
    sectionLabel?: string
  ) => void;
}

export function PickerPopover({
  items,
  selectedKey,
  focusedIndex,
  pickerId,
  label,
  popoverStyle,
  listboxRef,
  onSelect,
  onItemKeyDown
}: PickerPopoverProps) {
  const listboxId = `${pickerId}-listbox`;

  // Helper function to check if we need a divider after current item
  const needsDividerAfter = (
    item: PickerItemType,
    nextItem: PickerItemType | null,
    isLastItem: boolean
  ) => {
    if (isLastItem) return false;
    if (!nextItem) return false;

    // Add divider before "Resolved" items
    if (nextItem.type === 'item' && nextItem.label === 'Resolved') return true;
    // Add divider after "Overall" items
    if (item.type === 'item' && item.label === 'Overall') return true;

    // Add divider after item if next item is a header (item → header)
    if (item.type === 'item' && nextItem.type === 'header') return true;
    // Add divider after header if next item is also a header (header → header)
    if (item.type === 'header' && nextItem.type === 'header') return true;
    // Add divider after header if next item is an item (header → item)
    if (item.type === 'header' && nextItem.type === 'item') return true;
    return false;
  };

  return (
    <ul
      ref={listboxRef}
      id={listboxId}
      className={styles['spectrum-picker-popover']}
      role="listbox"
      aria-labelledby={label ? `${pickerId}-label` : undefined}
      tabIndex={-1}
      style={popoverStyle}
    >
      {(() => {
        let itemIndex = -1;
        return items.map((item, sectionIndex) => {
          const isLastItem = sectionIndex === items.length - 1;
          const nextItem =
            sectionIndex < items.length - 1 ? items[sectionIndex + 1] : null;

          if (item.type === 'item') {
            itemIndex++;
            const currentIndex = itemIndex;
            const isSelected = item.id === selectedKey;
            const isFocused = currentIndex === focusedIndex;

            return (
              <React.Fragment key={item.id}>
                <PickerItem
                  item={item}
                  isSelected={isSelected}
                  isFocused={isFocused}
                  onSelect={onSelect}
                  onKeyDown={onItemKeyDown}
                />
                {needsDividerAfter(item, nextItem, isLastItem) && (
                  <PickerDivider />
                )}
              </React.Fragment>
            );
          } else if (item.type === 'header' && item.children) {
            return (
              <React.Fragment key={item.id}>
                <PickerSectionHeader label={item.label} />
                {item.children.map(child => {
                  if (child.type === 'item') {
                    itemIndex++;
                    const currentIndex = itemIndex;
                    const isSelected = child.id === selectedKey;
                    const isFocused = currentIndex === focusedIndex;

                    return (
                      <PickerItem
                        key={child.id}
                        item={child}
                        isSelected={isSelected}
                        isFocused={isFocused}
                        onSelect={onSelect}
                        onKeyDown={onItemKeyDown}
                        sectionLabel={item.label}
                      />
                    );
                  }
                  return null;
                })}
                {needsDividerAfter(item, nextItem, isLastItem) && (
                  <PickerDivider />
                )}
              </React.Fragment>
            );
          }
          return null;
        });
      })()}
    </ul>
  );
}
